// Feedback detail (handoff 7.4). Status header + timeline + Luo answer + attachments
// + actions per the single state-to-action matrix (DESIGN.md 10.8, AC-B-020/021).
//
//   awaiting_info -> reply textarea + "提交补充信息" (-> under_review)
//   resolved      -> "确认已解决" (-> confirmed, terminal) + "重新打开" (-> reopened)
//   all others    -> no customer action (status + timeline only)
//
// Illegal transitions return 409 from the API; the UI shows the error and keeps
// the current state. All user content renders as escaped text (AC-B-004).

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { AppBar } from '../components/AppBar';
import { StatusBadge } from '../components/StatusBadge';
import { StatusTimeline } from '../components/StatusTimeline';
import { LuoAnswerCard } from '../components/LuoAnswerCard';
import { AttachmentList } from '../components/AttachmentChip';
import { BottomActionBar } from '../components/BottomActionBar';
import { ShareButton } from '../components/ShareButton';
import { CardSkeleton } from '../components/Skeletons';
import { Warning } from '../lib/icons';
import { api, ApiError } from '../lib/api';
import { useScreenEnter } from '../lib/motion';
import type { Feedback, FeedbackStatus } from '../lib/types';

const STATUS_COPY: Record<FeedbackStatus, string> = {
  submitted: '已提交，等待审核',
  under_review: '我们正在分析反馈…',
  awaiting_info: '请补充我们需要的信息',
  in_progress: '已生成变更请求，转入实现侧',
  resolved: '已修复，请确认是否符合预期',
  confirmed: '已确认关闭',
  reopened: '已重新打开，等待审核',
};

const REPLY_MAX = 1000;

export function Detail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [fb, setFb] = useState<Feedback | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);

  const load = () => {
    setError(null);
    setFb(null);
    api
      .getFeedback(id)
      .then(setFb)
      .catch((e: ApiError) => setError(e));
  };

  useEffect(load, [id]);

  const runAction = (fn: (id: string) => Promise<Feedback>) => async () => {
    setActionError(null);
    setSubmitting(true);
    try {
      const updated = await fn(id);
      setFb(updated);
      setReply('');
      setRetryAfter(null);
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 429 && err.retryAfter) {
        setRetryAfter(err.retryAfter);
        setActionError(`操作过于频繁，请 ${err.retryAfter} 秒后重试`);
      } else if (err.status === 409) {
        setActionError(err.message || '当前状态不允许该操作');
      } else {
        setActionError(err.message || '操作失败，请重试');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = runAction((fid) => api.submitMoreInfo(fid, reply.trim()));
  const confirmResolved = runAction((fid) => api.confirmResolved(fid));
  const reopen = runAction((fid) => api.reopenFeedback(fid));

  const replyInvalid = reply.trim().length === 0;

  return (
    <>
      <AppBar title={fb?.summary ?? '反馈详情'} onBack={() => navigate('/list')} trailing={<ShareButton />} />
      <main className="app-main">
        <motion.section
          className="section"
          {...useScreenEnter()}
          style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
          aria-live="polite"
        >
          {error && (
            <div className="error-banner" role="alert">
              <Warning weight="regular" aria-hidden="true" />
              <span>{error.message}</span>
              <button type="button" className="btn btn-ghost" style={{ height: 32, marginLeft: 'auto' }} onClick={load}>
                重试
              </button>
            </div>
          )}

          {fb === null && !error && <CardSkeleton />}

          {fb && (
            <>
              <h1 className="sr-only">{fb.summary}</h1>

              {/* Status header */}
              <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <StatusBadge status={fb.status} withPulse={fb.status === 'under_review'} />
                <p style={{ fontSize: '0.875rem', color: 'var(--text-2)' }}>{STATUS_COPY[fb.status]}</p>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <span className="mono muted" style={{ fontSize: '0.75rem' }}>编号 {fb.id}</span>
                  <span className="mono muted" style={{ fontSize: '0.75rem' }}>{fb.org} · {fb.project}</span>
                </div>
              </div>

              {/* Detail (escaped text) */}
              <div className="card">
                <h2 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>反馈详情</h2>
                <p className="prose-measure" style={{ whiteSpace: 'pre-wrap', color: 'var(--text)', fontSize: '0.9375rem' }}>
                  {fb.detail}
                </p>
                <AttachmentList attachments={fb.attachments} />
              </div>

              {/* Luo answer */}
              {fb.luoAnswer && <LuoAnswerCard answer={fb.luoAnswer} />}

              {/* More-info reply (awaiting_info only) */}
              {fb.status === 'awaiting_info' && (
                <div className="callout callout-warning" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div className="field">
                    <label htmlFor="reply" className="label">补充回复</label>
                    <textarea
                      id="reply"
                      name="reply"
                      autoComplete="off"
                      className="textarea"
                      placeholder="补充我们需要的信息，例如复现步骤、截图说明"
                      value={reply}
                      onChange={(e) => setReply(e.target.value.slice(0, REPLY_MAX))}
                      maxLength={REPLY_MAX}
                      aria-describedby="reply-help reply-count"
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span id="reply-help" className="helper">回复后将重新进入审核。</span>
                      <span id="reply-count" className="counter">{reply.length}/{REPLY_MAX}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div>
                <h2 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>状态历史</h2>
                <StatusTimeline entries={fb.timeline} />
              </div>

              {actionError && (
                <div className="error-banner" role="alert">
                  <Warning weight="regular" aria-hidden="true" />
                  <span>{actionError}</span>
                </div>
              )}

              {fb.status === 'awaiting_info' && (
                <div className="hidden md:flex md:justify-end">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={submitting || replyInvalid || retryAfter !== null}
                    onClick={submitReply}
                  >
                    {submitting ? '提交中…' : '提交补充信息'}
                  </button>
                </div>
              )}

              {fb.status === 'resolved' && (
                <div className="hidden md:flex md:justify-end" style={{ gap: '0.625rem' }}>
                  <button type="button" className="btn btn-secondary" disabled={submitting} onClick={reopen}>
                    {submitting ? '处理中…' : '重新打开'}
                  </button>
                  <button type="button" className="btn btn-primary" disabled={submitting} onClick={confirmResolved}>
                    {submitting ? '处理中…' : '确认已解决'}
                  </button>
                </div>
              )}
            </>
          )}
        </motion.section>
      </main>

      {fb && fb.status === 'awaiting_info' && (
        <BottomActionBar>
          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={submitting || replyInvalid || retryAfter !== null}
            onClick={submitReply}
          >
            {submitting ? '提交中…' : '提交补充信息'}
          </button>
        </BottomActionBar>
      )}

      {fb && fb.status === 'resolved' && (
        <BottomActionBar>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ flex: '1 1 0' }}
            disabled={submitting}
            onClick={reopen}
          >
            {submitting ? '处理中…' : '重新打开'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ flex: '2 1 0' }}
            disabled={submitting}
            onClick={confirmResolved}
          >
            {submitting ? '处理中…' : '确认已解决'}
          </button>
        </BottomActionBar>
      )}
    </>
  );
}
